/**
 * /agriculteurs/diagnostic-cee — Diagnostic primes énergie agricoles.
 *
 * Page outil dédiée aux agriculteurs avec questions métier spécifiques :
 *   - Orientation d'exploitation
 *   - SIRET (identification INSEE)
 *   - Infrastructures (serres + bâtiments d'élevage + stockage)
 *   - Travaux envisagés (avec codes fiches CEE)
 *   - Contact + estimation
 */

import {
  ArrowRight,
  BadgeCheck,
  Beef,
  CheckCircle2,
  Flower,
  HelpCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  Tractor,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../../components/layout/Container';
import { ToolPageSubHeader } from '../../../components/layout/ToolPageLayout';
import { MeshBackground } from '../../../components/motion/MeshBackground';
import { Reveal } from '../../../components/motion/Reveal';
import { Button } from '../../../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import { AgriDiagnosticFunnel } from '../../../components/tools/AgriDiagnosticFunnel';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../../../components/seo/jsonld/FAQPage';
import { cn } from '../../../lib/cn';

/* ─── Catégories d'aides couvertes (sidebar) ───────────── */

interface AideShort {
  label: string;
  amount: string;
  tone: 'brand' | 'accent' | 'lavender' | 'sage';
}

const AIDS_SHORT: AideShort[] = [
  { label: 'CEE AGRI-EQ', amount: 'Équipements & serres', tone: 'brand' },
  { label: 'CEE AGRI-TH', amount: 'Thermique élevage', tone: 'accent' },
  { label: 'CEE AGRI-UT', amount: 'Utilités industrielles', tone: 'lavender' },
  { label: 'CEE AGRI-SE', amount: 'Services & réglages', tone: 'sage' },
];

const TONE_STYLES = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  lavender: 'bg-lavender-50 text-lavender-700 ring-lavender-200/60',
  sage: 'bg-sage-100 text-sage-700 ring-sage-200/60',
} as const;

/* ─── Trust signals ─────────────────────────────────────── */

interface TrustSignal {
  icon: LucideIcon;
  label: string;
  detail: string;
}

const TRUST_SIGNALS: TrustSignal[] = [
  {
    icon: ShieldCheck,
    label: 'Fiches CEE AGRI officielles',
    detail: '51ᵉ arrêté CEE — catalogue ecologie.gouv.fr en vigueur 2026.',
  },
  {
    icon: BadgeCheck,
    label: 'Mandataire CEE agréé',
    detail: 'Habilitation préfectorale pour déposer vos dossiers CEE agricoles.',
  },
  {
    icon: Tractor,
    label: 'Conseillers énergie agricole',
    detail: "Spécialistes des dossiers AGRI · cumul PCAE & FranceAgriMer.",
  },
];

/* ─── Étapes ────────────────────────────────────────────── */

const STEPS = [
  { num: '1', title: 'Orientation', description: 'Type principal de production de votre exploitation.' },
  { num: '2', title: 'SIRET', description: 'Identification via le registre SIRENE.' },
  { num: '3', title: 'Infrastructures', description: 'Serres, élevage, stockage — questions conditionnelles.' },
  { num: '4', title: 'Travaux', description: 'Postes envisagés avec codes fiches CEE AGRI associés.' },
  { num: '5', title: 'Contact', description: 'Nom, prénom, téléphone, adresse de l\'exploitation.' },
  { num: '6', title: 'Estimation', description: 'Primes chiffrées + suggestions adaptées à votre profil.' },
];

/* ─── Questions clés ────────────────────────────────────── */

const KEY_QUESTIONS: Array<{
  icon: LucideIcon;
  title: string;
  questions: string[];
}> = [
  {
    icon: Flower,
    title: 'Serres',
    questions: [
      'Maraîchère, horticole ou mixte ?',
      'Chauffée ou non — combustible utilisé ?',
      'Type de couverture (verre, polycarbonate, tunnel) ?',
      'Régulation climatique automatisée ?',
    ],
  },
  {
    icon: Beef,
    title: 'Élevage',
    questions: [
      'Bovins lait, viande, porcins, volailles, ovins ?',
      'Effectif approximatif ?',
      'Tank à lait + salle de traite ?',
      'Ventilation et chauffage de bâtiment ?',
    ],
  },
  {
    icon: Warehouse,
    title: 'Stockage',
    questions: [
      'Matériel agricole ou produits à revendre ?',
      'Céréales en silo / vrac ?',
      'Fruits, vin, fromage — stockage réfrigéré ?',
      'Surface totale et conservation ?',
    ],
  },
];

/* ─── FAQ ───────────────────────────────────────────────── */

const FAQ_ITEMS = [
  {
    question: "Mon exploitation est-elle éligible aux CEE secteur AGRI ?",
    answer:
      "Toute exploitation immatriculée avec un code NAF agricole (010X à 030X) est éligible aux fiches CEE de la famille AGRI. Le diagnostic SIRET vérifie automatiquement votre éligibilité depuis le registre SIRENE.",
  },
  {
    question: 'Quelles sont les principales fiches CEE pour les serres ?',
    answer:
      "AGRI-EQ-101 (régulation climatique), AGRI-EQ-102 (double écran thermique), AGRI-EQ-104 (écrans latéraux), AGRI-EQ-107 (isolation parois), AGRI-EQ-109 (couverture performante), AGRI-TH-109 (récupérateur condensation), AGRI-TH-117 (déshumidificateur thermodynamique), AGRI-TH-118/119 (chauffage et déshu spécifique serre).",
  },
  {
    question: "Et pour l'élevage laitier ?",
    answer:
      "AGRI-TH-103 (pré-refroidisseur de lait), AGRI-TH-104 (récupération chaleur sur groupe froid), AGRI-TH-105 (récupérateur sur tank à lait), AGRI-TH-108 (PAC air/eau ou eau/eau). Les CEE peuvent se cumuler avec les aides PCAE de votre Région.",
  },
  {
    question: 'Les CEE sont-elles cumulables avec le PCAE et FranceAgriMer ?',
    answer:
      "Oui — c'est même recommandé. Les CEE financent l'équipement, le PCAE (Plan de Compétitivité et d'Adaptation des Exploitations) finance la modernisation globale, et FranceAgriMer cible les filières (photovoltaïque sur serre, cogénération, etc.). Notre équipe vous oriente vers la combinaison la plus avantageuse.",
  },
  {
    question: 'Mes données sont-elles partagées immédiatement ?',
    answer:
      "Non — toutes les questions sur votre exploitation (serres, animaux, stockage, travaux) restent côté navigateur jusqu'à l'étape « coordonnées ». À ce moment seulement, les données sont transmises à Solution Éco France pour vous adresser votre estimation détaillée et préparer votre dossier (RGPD art. 6.1.b).",
  },
];

export default function AgriculteursDiagnosticCeePage() {
  return (
    <div className="relative bg-paper">
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          {
            name: 'Espace agriculteurs',
            url: 'https://solutionecofrance.fr/agriculteurs',
          },
          {
            name: 'Diagnostic primes énergie agricoles',
            url: 'https://solutionecofrance.fr/agriculteurs/diagnostic-cee',
          },
        ]}
      />
      <FAQPageLd items={FAQ_ITEMS} />

      {/* ── Sous-barre outil ── */}
      <ToolPageSubHeader
        toolLabel="Diagnostic CEE agricole"
        backHref="/agriculteurs"
        backLabel="Espace agriculteurs"
        rightSlot={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
            <Tractor className="h-3 w-3" strokeWidth={2} />
            ~3 min
          </span>
        }
      />

      {/* ── Hero éditorial ── */}
      <section className="relative overflow-hidden border-b border-stone-200/60">
        <MeshBackground variant="warm" intensity="soft" withGrid />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(16,185,129,0.08),transparent)]"
        />
        <Container className="relative pt-12 pb-10 md:pt-16 md:pb-14">
          <div className="mx-auto max-w-4xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200/60 bg-brand-50/60 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-700 backdrop-blur-sm">
                <Sparkles
                  className="h-3 w-3 text-accent-500"
                  strokeWidth={2.4}
                />
                Diagnostic agricole · primes énergie 2026
              </span>
              <h1 className="mt-5 h-hero">
                Vos primes CEE agricoles,{' '}
                <span className="bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-transparent">
                  diagnostiquées en 3 minutes
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-stone-600 md:text-[16px]">
                Un diagnostic métier précis qui s'adapte à votre exploitation —
                serres, bâtiments d'élevage, stockage, équipements. Codes
                fiches CEE officielles + estimation chiffrée + cumul possible
                PCAE / FranceAgriMer.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <ul className="mx-auto mt-6 flex max-w-3xl flex-wrap justify-center gap-2">
                {[
                  '5 orientations couvertes',
                  '20+ opérations agricoles éligibles',
                  'Questions conditionnelles',
                  'Sans inscription',
                ].map((benefit) => (
                  <li
                    key={benefit}
                    className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white/90 px-3 py-1.5 text-[12px] font-medium text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] backdrop-blur-sm"
                  >
                    <CheckCircle2
                      className="h-3.5 w-3.5 text-brand-600"
                      strokeWidth={2.4}
                    />
                    {benefit}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ── Layout principal : Funnel + Sidebar ── */}
      <section className="py-8 md:py-12">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-12">
            <div className="min-w-0">
              <AgriDiagnosticFunnel />
            </div>

            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              {/* Familles CEE couvertes */}
              <div className="rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]">
                <div className="flex items-center gap-2">
                  <ShieldCheck
                    className="h-3.5 w-3.5 text-stone-400"
                    strokeWidth={1.8}
                  />
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    Familles CEE couvertes
                  </p>
                </div>
                <ul className="mt-3 space-y-2">
                  {AIDS_SHORT.map((aid) => (
                    <li
                      key={aid.label}
                      className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 ring-1 ring-inset ring-stone-100"
                    >
                      <span className="font-mono text-[12px] font-semibold text-ink">
                        {aid.label}
                      </span>
                      <span
                        className={cn(
                          'rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                          TONE_STYLES[aid.tone],
                        )}
                      >
                        {aid.amount}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-[11px] leading-relaxed text-stone-500">
                  Source : 51ᵉ arrêté CEE — ecologie.gouv.fr.
                </p>
              </div>

              {/* Garanties */}
              <div className="rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                  Nos garanties
                </p>
                <ul className="mt-3 space-y-3">
                  {TRUST_SIGNALS.map((t) => {
                    const Icon = t.icon;
                    return (
                      <li key={t.label} className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/50">
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-ink">
                            {t.label}
                          </p>
                          <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
                            {t.detail}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Contact expert */}
              <div className="overflow-hidden rounded-2xl border border-stone-200/70 bg-gradient-to-br from-ink via-stone-900 to-ink p-5 text-ivory-50 shadow-[0_8px_24px_-8px_rgba(10,10,10,.30)]">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm">
                    <HelpCircle
                      className="h-4 w-4 text-accent-300"
                      strokeWidth={1.8}
                    />
                  </span>
                  <p className="text-[13px] font-semibold">
                    Conseiller agricole
                  </p>
                </div>
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-stone-300">
                  Un spécialiste des dossiers AGRI vous accompagne dans le
                  cumul CEE + PCAE + FranceAgriMer. Réponse sous 24 h ouvrées.
                </p>
                <a
                  href="/contact?source=diagnostic-cee-agri"
                  className="mt-3.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-[12.5px] font-semibold text-ivory-50 backdrop-blur-sm transition-colors hover:bg-white/20"
                >
                  <Phone className="h-3.5 w-3.5" strokeWidth={2} />
                  Parler à un conseiller
                </a>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* ── Aperçu des questions métier ── */}
      <section className="border-y border-stone-200/60 bg-stone-50/60 py-14 md:py-20">
        <Container>
          <Reveal>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
                Questions calibrées pour votre métier
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                Un questionnaire adapté à chaque type d'exploitation
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-stone-600">
                Pas de questions génériques. On vous interroge uniquement sur
                ce qui est pertinent pour <strong>votre activité agricole</strong>.
              </p>
            </div>
          </Reveal>

          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
            {KEY_QUESTIONS.map((kq, idx) => {
              const Icon = kq.icon;
              return (
                <Reveal key={kq.title} delay={0.06 * idx}>
                  <div className="rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <h3 className="mt-3 font-display text-[15px] font-bold tracking-tight text-ink">
                      {kq.title}
                    </h3>
                    <ul className="mt-3 space-y-1.5">
                      {kq.questions.map((q) => (
                        <li
                          key={q}
                          className="flex items-start gap-2 text-[12.5px] leading-relaxed text-stone-600"
                        >
                          <CheckCircle2
                            className="mt-0.5 h-3 w-3 shrink-0 text-brand-500"
                            strokeWidth={2.2}
                          />
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Comment ça marche ── */}
      <section className="py-14 md:py-20">
        <Container>
          <Reveal>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
                En 6 étapes
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                Comment fonctionne le diagnostic
              </h2>
            </div>
          </Reveal>

          <div className="relative mx-auto max-w-6xl">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-[22px] hidden h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent lg:block"
            />
            <div className="grid gap-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-6">
              {STEPS.map((step) => (
                <Reveal key={step.num} delay={Number(step.num) * 0.04}>
                  <div>
                    <span className="relative flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white font-mono text-[13px] font-bold text-ink shadow-[0_1px_3px_rgba(10,10,10,.06)]">
                      {step.num}
                    </span>
                    <h3 className="mt-4 font-display text-[15px] font-semibold tracking-tight text-ink">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
                      {step.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section className="border-t border-stone-200/60 py-14 md:py-20">
        <Container size="narrow">
          <Reveal>
            <div className="mb-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
                FAQ
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                Questions fréquentes
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <Accordion
              type="single"
              collapsible
              className="rounded-2xl border border-stone-200 bg-white px-6 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]"
            >
              {FAQ_ITEMS.map((item, idx) => (
                <AccordionItem key={item.question} value={`q-${idx}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild variant="brand" size="lg" className="gap-2">
                <a href="#agri-funnel-top">
                  Démarrer le diagnostic
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="secondary" size="lg">
                <a href="/contact?source=diagnostic-cee-agri">
                  Parler à un conseiller
                </a>
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── Trust strip final ── */}
      <div className="border-t border-stone-200/70 bg-stone-50/70 py-8">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[12.5px] text-stone-500">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ShieldCheck
                className="h-3.5 w-3.5 text-brand-500"
                strokeWidth={2.2}
              />
              Catalogue CEE AGRI 2026 — 51ᵉ arrêté
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Tractor
                className="h-3.5 w-3.5 text-brand-500"
                strokeWidth={2.2}
              />
              Cumul PCAE & FranceAgriMer
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <BadgeCheck
                className="h-3.5 w-3.5 text-brand-500"
                strokeWidth={2.2}
              />
              Mandataire CEE habilité préfecture
            </span>
          </div>
        </Container>
      </div>
    </div>
  );
}
